import React, { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { LoadingType, setAccount, setLoadingState } from 'prequalificationV2/actions';
import AccountForm, { AccountFormData } from 'prequalificationV2/components/AccountForm/AccountForm';
import FormHeader from 'prequalificationV2/components/FormHeader';
import Loader from 'components/Loader';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { identityFullStory } from 'prequalificationV2/utils/fullStory';
import { navigateAfterLogin } from 'prequalificationV2/utils/navigateAfterLogin';
import { maskMonetaryValue } from 'prequalificationV2/utils/stringUtils';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import AuthApi from 'api/auth/AuthApi';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'prequalificationV2/routes/routes.utils';
import { CreateAccountView } from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/createAccount.enums';
import { getBorrowerFullNames } from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/createAccount.utils';
import { useCreateAccountOffersSummary } from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/useCreateAccountOffersSummary';
import styles from 'prequalificationV2/pages/SignInProcess/steps/CreateAccount/CreateAccount.module.scss';

const NoOffersHint = () => (
  <p className={styles.hintText}>
    We are looking forward to helping you with your loan needs. We are going to have one of our Customer Success
    Managers reach out to you to help you find the best loan for you. To proceed with finding out what options are
    available to you, please create an account using the form below. We will be reaching out to you by email, phone, or
    by messages through this account.
  </p>
);

const OffersAvailableHint = () => (
  <p className={styles.hintText}>
    In order to view your offers, you will need to create an account and talk to one of our Customer Success Managers to
    find the right loan offer for you. Use the form below to create your account and continue.
  </p>
);

const CreateAccount = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const {
    borrowerId,
    email,
    phone,
    isLocked,
    firstName: borrowerFirstName,
    lastName: borrowerLastName,
  } = useAppSelector((state) => state.borrower);
  const { firstName: coborrowerFirstName, lastName: coborrowerLastName } = useAppSelector((state) => state.coBorrower);

  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const offersSummary = useCreateAccountOffersSummary();

  const handleSubmit = async (data: AccountFormData) => {
    try {
      if ((email || data.email) && data.password && data.password === data.confirmPassword) {
        if (!borrowerId) {
          notificationManager.notifyFailure(FailureNotificationType.UnknownError);

          return;
        }

        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const userEmail = (email || data.email)!;
        const authApi = new AuthApi();

        const tokens = await authApi.createAccount(userEmail, data.password, phone, borrowerId);

        localStorage.setItem('token', tokens!.accessToken);
        localStorage.setItem('refreshToken', tokens!.refreshToken);

        identityFullStory(email, email);

        dispatch(setAccount({ email: email }));

        await navigateAfterLogin({ isLocked: !!isLocked, dispatch: dispatch, navigator: navigator });
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  };

  const formMethods = useForm({
    defaultValues: {
      email: email,
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.CreateAccount,
          prequalificationVersion: PrequalificationVersion.Second,
          borrowerId: borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const renderInfoMessage = () => {
    if (offersSummary.view === CreateAccountView.ManualNoOffers) {
      return (
        <div className={styles.infoContainer}>
          <div className={styles.infoContent}>You are nearly there!</div>
        </div>
      );
    }

    if (offersSummary.view === CreateAccountView.OffersAvailable) {
      const fullNames = getBorrowerFullNames(
        borrowerFirstName,
        borrowerLastName,
        coborrowerFirstName,
        coborrowerLastName,
      );

      return (
        <div className={styles.infoContainer}>
          <div className={styles.infoContent}>
            Congratulations {fullNames}! You qualify for offers up to{' '}
            <span className={styles.maxLoanAmount}>{maskMonetaryValue(offersSummary.maxLoanAmount)}</span> with a fixed
            rate as low as <span className={styles.maxLoanAmount}>{offersSummary.lowestRate}%</span>!
          </div>
        </div>
      );
    }

    return null;
  };

  const getHint = () => {
    if (offersSummary.view === CreateAccountView.ManualNoOffers) {
      return <NoOffersHint />;
    }

    if (offersSummary.view === CreateAccountView.OffersAvailable) {
      return <OffersAvailableHint />;
    }

    return undefined;
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div>
          {renderInfoMessage()}
          <FormHeader title="Create Account" />
          <FormProvider {...formMethods}>
            <AccountForm
              passwordLabel="Create Password"
              showEmailField
              showPasswordField
              showConfirmPasswordField
              showPasswordCaption
              buttonLabel="Submit"
              onSubmit={handleSubmit}
              buttonRowClassname={styles.buttonRow}
              hintElement={getHint()}
            />
          </FormProvider>
        </div>
      )}
    </>
  );
};

export default CreateAccount;
